import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Joyride, STATUS } from "react-joyride";
import { useAuth } from "@/contexts/AuthContext";

/**
 * R153 — First-login product tour.
 * R157 — Full page-level tours for Dashboard, Dues, Estoppel, Special Assessments.
 *
 * `useTour().start(name)` — kicks off a named tour. Named tours live in
 * `TOURS` below. Auto-fires the role-appropriate onboarding tour once
 * per user (persisted in localStorage keyed on user.id).
 */
const TourContext = createContext(null);
export const useTour = () => useContext(TourContext);

const TOURS = {
  admin_onboarding: [
    { target: "[data-tour='sidebar-communities']",
      content: "Manage every community your firm operates from here. Add or rename properties, upload governing docs, and set per-property fee cadences.",
      disableBeacon: true },
    { target: "[data-tour='sidebar-financials']",
      content: "Finance sits under one group: chart of accounts, GL, dues schedule, estoppel certificates, bank rec, trust accounting, and 1099s." },
    { target: "[data-tour='sidebar-dues']",
      content: "R151 Dues Portal. Configure the tier table + late fees, run daily generation, and let residents self-pay." },
    { target: "[data-tour='sidebar-whats-new']",
      content: "Check the What's New page for every release we've shipped — great fodder for board reports." },
  ],
  property_manager_onboarding: [
    { target: "[data-tour='sidebar-communities']",
      content: "Communities you've been assigned to appear here. Click one to switch focus.",
      disableBeacon: true },
    { target: "[data-tour='sidebar-financials']",
      content: "Post journal entries, track rent, and run bank rec." },
    { target: "[data-tour='sidebar-vendors']",
      content: "Vendor directory tracks COI + W-9 expiration — every vendor without a valid COI is auto-nudged 60/30/7 days before expiry." },
  ],
  resident_onboarding: [
    { target: "[data-tour='sidebar-dashboard']",
      content: "Your dashboard shows dues, work orders, guest passes, and announcements.",
      disableBeacon: true },
    { target: "[data-tour='sidebar-dues']",
      content: "Pay HOA dues here. Card + ACH accepted. Autopay saves you from late fees." },
    { target: "[data-tour='sidebar-documents']",
      content: "Community rules, bylaws, and meeting minutes are all here." },
  ],

  // R157 — page-level tours (fired from a "Take a tour" chip on each page)
  dashboard_tour: [
    { target: "[data-tour='dashboard-kpis']", disableBeacon: true,
      title: "KPI strip",
      content: "Four numbers that matter every morning: residents (with occupancy), MTD revenue, total delinquent, and pending approvals waiting on your review." },
    { target: "[data-tour='dashboard-collections']",
      title: "Collections trend",
      content: "Six-month collected-vs-delinquent bar chart. If the grey bar starts creeping up, jump to Dues → Delinquency to see who's slipping." },
    { target: "[data-tour='dashboard-aging']",
      title: "Aging buckets",
      content: "Total dollars sitting in each aging bucket. Anything past 60 days is a candidate for a demand letter (Dues → Delinquency Letters)." },
    { target: "[data-tour='dashboard-packet']",
      title: "Board packet",
      content: "One-click PDF of the whole month's story. Sign & Ratify embeds your typed or drawn signature with a cryptographic hash for the audit trail." },
  ],
  dues_tour: [
    { target: "[data-tour='dues-scope']", disableBeacon: true,
      title: "Pick a community",
      content: "Every dues schedule is per-property. Switch here to configure a different community's tiers, overrides, and late fees." },
    { target: "[data-tour='dues-tiers']",
      title: "Tier by unit type",
      content: "Set a rate per unit type (1BR, 2BR, Penthouse…). The `default` tier is required — it's the fallback for any unit whose type isn't matched." },
    { target: "[data-tour='dues-test-checkout']",
      title: "$1 test checkout",
      content: "Fire a real $1 Stripe Checkout to prove your keys + webhook are wired end-to-end. Marked `admin_test` so it never touches a resident ledger." },
    { target: "[data-tour='dues-preview']",
      title: "Preview → Generate",
      content: "Preview shows exactly what tonight's cron will bill. Generate now runs the same code — idempotent, so re-runs are safe." },
  ],
  estoppel_tour: [
    { target: "[data-tour='estoppel-new']", disableBeacon: true,
      title: "Manual certificate",
      content: "Kick off a fresh estoppel for any unit — you'll fill closing details, preview the live balance, then commit a title-company-ready PDF snapshot." },
    { target: "[data-tour='estoppel-history']",
      title: "History table",
      content: "Every issued certificate lives here — download the PDF, re-email a copy to a title company, or rescind if the closing falls through." },
  ],
  special_assessments_tour: [
    { target: "[data-tour='sa-new']", disableBeacon: true,
      title: "Draft an assessment",
      content: "Roof replacement, hurricane damage, reserve top-up. Draft → Approve → Bill splits the total across every unit by the allocation method you pick." },
    { target: "[data-tour='sa-history']",
      title: "Track the lifecycle",
      content: "Draft (edit freely) → Approved (locked) → Billed (charges appear on ledgers). Cancel voids any open charges." },
  ],
  stripe_walkthrough_tour: [
    { target: "[data-tour='stripe-step-1']", disableBeacon: true,
      title: "Step 1 — Verify keys",
      content: "Reads STRIPE_SECRET_KEY from the pod env and pings Stripe's API. Green here means your keys are live and reachable." },
    { target: "[data-tour='stripe-step-2']",
      title: "Step 2 — Open test checkout",
      content: "Fires a real $1 Stripe Checkout Session tagged `admin_test`. Use test card 4242 4242 4242 4242 with any future date + any CVC." },
    { target: "[data-tour='stripe-step-3']",
      title: "Step 3 — Confirm webhook",
      content: "After you complete the checkout, we poll `/api/webhooks/stripe/events` — the webhook must fire for the flow to be considered wired." },
  ],
};


export function TourProvider({ children }) {
  const { user } = useAuth() || {};
  const [active, setActive] = useState(null);
  const storageKey = user?.id ? `pmp:tour_done:${user.id}` : null;

  const start = useCallback((name) => {
    if (TOURS[name]) setActive(name);
  }, []);

  const stop = useCallback(() => setActive(null), []);

  // Auto-fire once per user based on role. Waits (up to ~4 seconds) until
  // at least the first data-tour target has mounted so Joyride can measure
  // and paint the beacon reliably even during a slow initial render.
  useEffect(() => {
    if (!user?.id || !storageKey) return;
    const done = (() => {
      try { return JSON.parse(localStorage.getItem(storageKey) || "[]"); }
      catch (e) { console.debug("tour: bad done list", e); return []; }
    })();
    let tour = null;
    if (user.role === "admin" || user.role === "property_manager") {
      const name = user.role === "admin" ? "admin_onboarding" : "property_manager_onboarding";
      if (!done.includes(name)) tour = name;
    } else if (user.role === "resident" || user.role === "owner") {
      if (!done.includes("resident_onboarding")) tour = "resident_onboarding";
    }
    if (!tour) return;

    const firstTarget = TOURS[tour]?.[0]?.target;
    let elapsed = 0;
    const interval = 250;
    const maxWait = 6000;
    const timer = setInterval(() => {
      elapsed += interval;
      const el = firstTarget ? document.querySelector(firstTarget) : null;
      if (el || elapsed >= maxWait) {
        clearInterval(timer);
        // small extra delay so Joyride paints AFTER the shell finishes layout
        setTimeout(() => setActive(tour), 250);
      }
    }, interval);
    return () => clearInterval(timer);
  }, [user, storageKey]);

  const steps = useMemo(() => (active ? TOURS[active] : []), [active]);

  const onCb = useCallback(
    (data) => {
      const { status } = data;
      if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
        if (storageKey && active) {
          try {
            const done = JSON.parse(localStorage.getItem(storageKey) || "[]");
            if (!done.includes(active)) done.push(active);
            localStorage.setItem(storageKey, JSON.stringify(done));
          } catch (e) { console.debug("tour: persist failed", e); }
        }
        setActive(null);
      }
    },
    [storageKey, active],
  );

  const ctxValue = useMemo(() => ({ start, stop, active }), [start, stop, active]);

  return (
    <TourContext.Provider value={ctxValue}>
      {children}
      <Joyride steps={steps} run={!!active} continuous showSkipButton
                showProgress disableScrolling
                locale={{ back: "Back", close: "Close", last: "Got it", next: "Next", open: "Open the dialog", skip: "Skip tour" }}
                styles={{
                  options: {
                    zIndex: 10000,
                    primaryColor: "#f97316",
                    backgroundColor: "#0a0a0b",
                    textColor: "#fafafa",
                    arrowColor: "#0a0a0b",
                    overlayColor: "rgba(0,0,0,0.6)",
                  },
                  tooltipContainer: { textAlign: "left" },
                  tooltipTitle: { fontSize: 14, color: "#f97316", marginBottom: 8 },
                  buttonNext: { borderRadius: 999 },
                  buttonBack: { color: "#fafafa" },
                }}
                callback={onCb} />
    </TourContext.Provider>
  );
}


/**
 * Small chip button pages drop into their PageHeader `actions` slot to
 * launch their own named tour. Keeps every page consistent.
 */
export function TourLauncherButton({ name, label = "Take a tour" }) {
  const tour = useTour();
  if (!tour) return null;
  return (
    <button
      type="button"
      onClick={() => tour.start(name)}
      data-testid={`tour-launch-${name}`}
      className="inline-flex items-center gap-1.5 rounded-full border border-orange-500/40 bg-orange-500/5 px-3 h-9 text-xs font-medium text-orange-200 hover:bg-orange-500/15 transition"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4M12 8h.01" />
      </svg>
      {label}
    </button>
  );
}
